import { useCallback, useState } from 'react'

import { useQueryClient } from 'react-query'
import { useHistory } from 'react-router'

import { useAppRoutes } from '@webapp/hooks'
import { useAttachInvoiceToMatchedItemsPresenter } from '@webapp/modules/accounts-payable/features/attach-invoice-to-matched-items/presenters/AttachInvoiceToMatchedItemsPresenter'
import { useCreditCardStatementDetailsPresenter } from '@webapp/modules/accounts-payable/features/get-credit-card-account/presenters/CreditCardStatementDetailsPresenter'
import { useBillMatchedItemListPresenter } from '@webapp/modules/accounts-payable/features/list-bill-matched-items/presenters/BillMatchedItemListPresenter'
import { MatchPageTabs } from '@webapp/modules/accounts-payable/pages/types'
import { RECONCILE_SESSION_BILL_FORM_DATA_BASE_KEY } from '@webapp/modules/accounts-payable/query-configs/reconcileSessionQueries'
import { type VendorPaymentMethod } from '@webapp/modules/vendor'

export interface IUseCreateCreditCardBillViewParams {
  accountId: number
}

export const useCreateCreditCardBillView = ({
  accountId,
}: IUseCreateCreditCardBillViewParams) => {
  const history = useHistory()
  const { url } = useAppRoutes()
  const queryClient = useQueryClient()

  // Presenter companion hook (hook-data only) — account + submit-target
  // session from the route id. Shared `creditCardAccountQueries.detail` cache.
  const { account, session, isLoading } =
    useCreditCardStatementDetailsPresenter({ accountId })

  // Matches forwarded from feature 21 to feature 23 (no extra fetch).
  const {
    data: { matches },
  } = useBillMatchedItemListPresenter({ sessionId: session?.id ?? 0 })

  const { commands: attachCommands, states: attachStates } =
    useAttachInvoiceToMatchedItemsPresenter({ matches })

  const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] =
    useState(false)
  const [attachInvoiceFileUuid, setAttachInvoiceFileUuid] =
    useState<Nullable<string>>(null)
  const [additionalPaymentMethods, setAdditionalPaymentMethods] = useState<
    VendorPaymentMethod[]
  >([])
  const [attachSelectedItemIds, setAttachSelectedItemIds] = useState<number[]>(
    []
  )

  const openPaymentMethodModal = useCallback(() => {
    setIsPaymentMethodModalOpen(true)
  }, [])

  const closePaymentMethodModal = useCallback(() => {
    setIsPaymentMethodModalOpen(false)
  }, [])

  // BHV-047: new method is auto-selected by feature 22 via the prop; the FS
  // also invalidates its own bill-form-data cache (ADR-6 boundary).
  const handlePaymentMethodCreated = useCallback(
    (method: VendorPaymentMethod) => {
      setAdditionalPaymentMethods((previous) => [...previous, method])
      setIsPaymentMethodModalOpen(false)
      queryClient.invalidateQueries({
        queryKey: RECONCILE_SESSION_BILL_FORM_DATA_BASE_KEY,
      })
    },
    [queryClient]
  )

  const openAttachInvoiceModal = useCallback((fileUuid: string) => {
    setAttachInvoiceFileUuid(fileUuid)
    setAttachSelectedItemIds([])
  }, [])

  const closeAttachInvoiceModal = useCallback(() => {
    setAttachInvoiceFileUuid(null)
    setAttachSelectedItemIds([])
  }, [])

  const toggleAttachItem = useCallback((itemId: number) => {
    setAttachSelectedItemIds((previous) =>
      previous.includes(itemId)
        ? previous.filter((id) => id !== itemId)
        : [...previous, itemId]
    )
  }, [])

  const confirmAttachInvoice = useCallback(async () => {
    if (!attachInvoiceFileUuid) return

    try {
      await attachCommands.attach(attachInvoiceFileUuid, attachSelectedItemIds)
      setAttachInvoiceFileUuid(null)
      setAttachSelectedItemIds([])
    } catch {
      // Failure keeps the modal open — the feature already toasted (ADR-10).
    }
  }, [attachCommands, attachInvoiceFileUuid, attachSelectedItemIds])

  // BHV-049: submitted bill → pending bills list.
  const redirectToPendingBills = useCallback(() => {
    history.push(url('BillListPage', { status: 'pending' }))
  }, [history, url])

  const goBackToMatch = useCallback(() => {
    history.push(
      url('ReconciliationMatchPage', {
        id: String(accountId),
        tab: MatchPageTabs.UNMATCHED,
      })
    )
  }, [accountId, history, url])

  return {
    state: {
      account,
      session,
      isLoading,
      matches,
      additionalPaymentMethods,
      isPaymentMethodModalOpen,
      attachInvoiceFileUuid,
      attachSelectedItemIds,
      isAttaching: attachStates.isAttaching,
    },
    methods: {
      openPaymentMethodModal,
      closePaymentMethodModal,
      handlePaymentMethodCreated,
      openAttachInvoiceModal,
      closeAttachInvoiceModal,
      toggleAttachItem,
      confirmAttachInvoice,
      redirectToPendingBills,
      goBackToMatch,
    },
  }
}
